import { FC, MutableRefObject, Suspense, useMemo, useState } from 'react';
import { useMutation } from 'react-relay';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { UserRoundPlus } from 'lucide-react';
import { Class } from '../../../models/Class';
import { Roles } from '../../../models/User';
import { addUsersToClass, removeUsersFromClass } from '../../../graphQLOperations/Classes';
import { ClassesAddUsersToClassMutation } from '../../../graphQLOperations/__generated__/ClassesAddUsersToClassMutation.graphql';
import { ClassesRemoveUsersFromClassMutation } from '../../../graphQLOperations/__generated__/ClassesRemoveUsersFromClassMutation.graphql';
import { ThreeDotLoader } from '../../Fallback/ThreeDotsLoader';
import ClassWizardCoTeacherStep from './ClassWizardCoTeacherStep';
import ClassWizardNewTeacherDialog from './ClassWizardNewTeacherDialog';
import { WizardUser } from './wizardTypes';

interface Props {
  visible: boolean;
  onHide: () => void;
  selectedClass: Class;
  toast: MutableRefObject<Toast | null>;
  onSaved: () => void;
}

const ClassManageCoTeachersDialog: FC<Props> = ({ visible, onHide, selectedClass, toast, onSaved }) => {
  const originalTeachers = useMemo<WizardUser[]>(
    () =>
      (selectedClass.users ?? [])
        .filter(user => user.isActive && user.roles?.some(role => role.name === Roles.TEACHER))
        .map(user => ({ id: user.id, firstName: user.firstName, lastName: user.lastName })),
    [selectedClass.users],
  );
  const [selected, setSelected] = useState<WizardUser[]>(originalTeachers);
  const [extraTeachers, setExtraTeachers] = useState<WizardUser[]>([]);
  const [newTeacherVisible, setNewTeacherVisible] = useState(false);

  const [addUsers, adding] = useMutation<ClassesAddUsersToClassMutation>(addUsersToClass);
  const [removeUsers, removing] = useMutation<ClassesRemoveUsersFromClassMutation>(removeUsersFromClass);
  const saving = adding || removing;

  const close = () => {
    setSelected(originalTeachers);
    onHide();
  };

  const showError = () =>
    toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Could not update co-teachers.' });

  const finish = () => {
    toast.current?.show({ severity: 'success', detail: 'Co-teachers updated.' });
    onSaved();
    onHide();
  };

  const save = () => {
    const toAdd = selected.filter(teacher => !originalTeachers.some(o => o.id === teacher.id));
    const toRemove = originalTeachers.filter(teacher => !selected.some(s => s.id === teacher.id));

    if (toAdd.length) {
      addUsers({
        variables: { input: { userIds: toAdd.map(t => t.id), classId: selectedClass.id } },
        onCompleted: () => {
          if (toRemove.length) {
            removeUsers({
              variables: { input: { userIds: toRemove.map(t => t.id), classId: selectedClass.id } },
              onCompleted: finish,
              onError: showError,
            });
          } else {
            finish();
          }
        },
        onError: showError,
      });
    } else if (toRemove.length) {
      removeUsers({
        variables: { input: { userIds: toRemove.map(t => t.id), classId: selectedClass.id } },
        onCompleted: finish,
        onError: showError,
      });
    } else {
      onHide();
    }
  };

  const header = (
    <div className="flex align-items-start gap-md">
      <span className="nc_wizard-header-icon">
        <UserRoundPlus size={17} aria-hidden />
      </span>
      <div className="flex flex-column">
        <h2 className="text-xl font-semibold line-height-1 m-0 tw-not-italic">Manage co-teachers</h2>
        <span className="text-color-secondary text-sm tw-leading-5 mt-1 tw-font-normal">
          Co-teachers can view and edit class details and rosters, and will have access to all class reports. Admins
          don&apos;t need to be added as co-teachers because they can see all classes at their site.
        </span>
      </div>
    </div>
  );

  const footer = (
    <div className="flex justify-content-between align-items-center gap-md w-full">
      <Button label="Cancel" outlined rounded onClick={close} disabled={saving} />
      <Button label="Save" icon="nc_icon-nc_save icon-sm" rounded onClick={save} loading={saving} />
    </div>
  );

  return (
    <>
      <Dialog
        header={header}
        footer={footer}
        visible={visible}
        onHide={close}
        className="nc_class-wizard"
        style={{ width: '38.75rem', minHeight: '36.0625rem' }}
        dismissableMask={false}
        ariaCloseIconLabel="Close"
      >
        <Suspense fallback={<ThreeDotLoader dark />}>
          <ClassWizardCoTeacherStep
            siteId={selectedClass.site?.id ?? ''}
            selected={selected}
            onChange={setSelected}
            onNewTeacher={() => setNewTeacherVisible(true)}
            extraTeachers={extraTeachers}
          />
        </Suspense>
      </Dialog>
      {newTeacherVisible && (
        <ClassWizardNewTeacherDialog
          visible
          onHide={() => setNewTeacherVisible(false)}
          siteId={selectedClass.site?.id ?? ''}
          siteName={selectedClass.site?.name ?? ''}
          toast={toast}
          onCreated={teacher => {
            setSelected(prev => [...prev, teacher]);
            setExtraTeachers(prev => [...prev, teacher]);
            setNewTeacherVisible(false);
          }}
        />
      )}
    </>
  );
};

export default ClassManageCoTeachersDialog;
